// Imports ------------------------------------------------------------------->

import { Service, service, inject } from './di'
import { ConfigService } from './ConfigService'
import {
    LoyaltyCard,
    LoyaltyCardJSON,
    LoyaltyProgramCoupon,
} from '../definitions/CommonDefinitions'
import { fetchRaw, fetchJSON } from '../utils/fetch'
import * as i18n from '../i18n/I18n'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

export interface SessionJSON {
    cards: {
        barcode_type: string
        card_id: string
        loyalty_program_id: number
        phone: string
        registered: boolean
        token: string
    }[]
    user_id: string
}

export interface ClippedCouponsJSON {
    loyalty_program_coupons: {
        id: number
        status: string // "clipped", "redeemed" or "unavailable"
    }[]
}

export interface RegisteredCardJSON {
    loyalty_program_id: number
    card_id: number
    token: string
    barcode: string
    barcode_type: string
    user_id: string
}

export enum PostcardMode {
    Default,
    PG,
}

@service
export class PostcardService extends Service {
    @inject config: ConfigService

    startSession(userId: string, cards: LoyaltyCardJSON): Promise<SessionJSON> {
        const serializedCards = cards.loyalty_cards.map((card) => ({
            loyalty_program_id: card.loyalty_program_id,
            card_id: card.card_id,
            phone: card.phone_number,
            token: card.token,
        }))
        const body = {
            cards: serializedCards,
            user_id: userId,
        }

        return fetchJSON<SessionJSON>(
            `${this.config.POSTCARD_SERVER}/start_session`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Accept', 'application/json'],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    getCardCoupons(userId: string, cards: LoyaltyCard[]) {
        const serializedCards = cards.map((card) => ({
            loyalty_program_id: card.loyaltyProgramId,
            token: card.token,
            card_id: card.cardId,
            phone_number: card.phoneNumber,
        }))
        const body = {
            cards: serializedCards,
            user_id: userId,
        }

        if (serializedCards.length === 0) {
            return Promise.resolve({
                loyalty_program_coupons: [],
            } as ClippedCouponsJSON)
        }

        return fetchJSON<ClippedCouponsJSON>(
            `${this.config.POSTCARD_SERVER}/loyalty_card_coupons`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Accept', 'application/json'],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    clipCoupon(
        userId: string,
        card: LoyaltyCard,
        coupon: LoyaltyProgramCoupon
    ) {
        const body: { [key: string]: string | number } = {
            loyalty_program_id: coupon.loyaltyProgramId,
            user_id: userId,
            coupon_id: coupon.couponId,
            loyalty_program_coupon_id: coupon.id,
        }
        if (card.phoneNumber) {
            body['phone'] = card.phoneNumber
        }
        if (card.cardId) {
            body['card_id'] = card.cardId
        }
        if (card.token) {
            body['token'] = card.token
        }
        if (coupon.externalId) {
            body['external_id'] = coupon.externalId
        }
        return fetchRaw(
            `${this.config.POSTCARD_SERVER}/loyalty_card_coupons/clip`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Accept', 'application/json'],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    registerLoyaltyCard(userId: string, card: LoyaltyCard) {
        const body: { [key: string]: string | number } = {
            loyalty_program_id: card.loyaltyProgramId,
            user_id: userId,
        }
        if (card.phoneNumber) {
            body['phone'] = card.phoneNumber
        }
        if (card.pinNumber) {
            body['pin'] = card.pinNumber
        }
        if (card.cardId) {
            body['card_id'] = card.cardId
        }

        return fetchJSON<RegisteredCardJSON>(
            `${this.config.POSTCARD_SERVER}/loyalty_cards/register`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Accept', 'application/json'],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    unregisterLoyaltyCard(userId: string, card: LoyaltyCard) {
        const body: { [key: string]: string | number } = {
            loyalty_program_id: card.loyaltyProgramId,
            user_id: userId,
        }
        if (card.phoneNumber) {
            body['phone'] = card.phoneNumber
        }
        if (card.pinNumber) {
            body['pin'] = card.pinNumber
        }
        if (card.cardId) {
            body['card_id'] = card.cardId
        }
        if (card.token) {
            body['token'] = card.cardId
        }

        return fetchRaw(
            `${this.config.POSTCARD_SERVER}/loyalty_cards/unregister`,
            {
                method: 'POST',
                headers: [
                    ['Content-Type', 'application/json'],
                    ['Accept', 'application/json'],
                ],
                body: JSON.stringify(body),
            }
        )
    }

    getErrorMessage(code: number) {
        /* tslint:disable align */
        switch (code) {
            case 11:
                return i18n.i18n('postcard_error_11')
            case 12:
                return i18n.i18n('postcard_error_12')
            case 13:
                return i18n.i18n('postcard_error_13')
            case 21:
                return i18n.i18n('postcard_error_21')
            default:
                return
        }
        /* tslint:enable align */
    }
}
